import { z } from 'zod';
import {
  ACCOUNT_ACTIONS_REQUIRING_REASON,
  AccountAction,
  CurrencyCode,
  DocumentType,
  MovementType,
  PaymentMethod,
  PaymentTerms,
  PromotionKind,
} from '../statuses';

/** Reusable primitives. */
export const idSchema = z.string().min(1).max(128);
export const isoDateSchema = z.string().datetime();
export const idempotencyKeySchema = z.string().min(1).max(128);

/**
 * Venezuelan RIF: a type letter (V, E, J, G, P or C), 8 digits and a check digit. Accepts
 * `J-12345678-9`, `j123456789` or `J 12345678 9` and always stores the canonical
 * `J-12345678-9`. Only the format is validated; the check digit is not recomputed.
 */
const RIF_PATTERN = /^([VEJGPC])[-\s]?(\d{8})[-\s]?(\d)$/;
export const rifSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(RIF_PATTERN, 'El RIF debe tener el formato J-12345678-9 (letra V, E, J, G, P o C, 8 dígitos y un dígito verificador).')
  .transform((rif) => rif.replace(RIF_PATTERN, '$1-$2-$3'));

/** Standard cursor-paginated list query. */
export const listQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  q: z.string().max(200).optional(),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

/** Product list: `scope=all` (admin only) also returns inactive and archived products. */
export const productListQuerySchema = listQuerySchema.extend({ scope: z.enum(['active', 'all']).default('active') });
export type ProductListQuery = z.infer<typeof productListQuerySchema>;

export const createCustomerSchema = z.object({
  name: z.string().min(2).max(160),
  rif: rifSchema,
  paymentTerms: z.nativeEnum(PaymentTerms).default(PaymentTerms.UPFRONT),
  creditLimitUsd: z.number().nonnegative().default(0),
  priceListId: idSchema.optional(),
  assignedSalesRepId: idSchema.optional(),
  territoryId: z.string().max(80).optional(),
  /** The distributor this customer is a sub-account of (admin only). */
  distributorId: idSchema.optional(),
  optInPromotions: z.boolean().default(true),
});
export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;

export const createProductSchema = z.object({
  brandId: idSchema,
  categoryId: idSchema,
  name: z.string().min(2).max(160),
  description: z.string().max(2000).optional(),
  active: z.boolean().default(true),
});
export type CreateProductInput = z.infer<typeof createProductSchema>;

export const orderItemInputSchema = z.object({
  skuId: idSchema,
  quantity: z.number().int().positive(),
});
export type OrderItemInput = z.infer<typeof orderItemInputSchema>;

export const createOrderSchema = z.object({
  customerId: idSchema,
  currency: z.nativeEnum(CurrencyCode).default(CurrencyCode.USD),
  /** VES per 1 USD, locked onto the order at creation. */
  exchangeRate: z.number().positive(),
  items: z.array(orderItemInputSchema).min(1),
  notes: z.string().max(1000).optional(),
  idempotencyKey: idempotencyKeySchema.optional(),
});
export type CreateOrderInput = z.infer<typeof createOrderSchema>;

export const createPaymentSchema = z.object({
  accountId: idSchema,
  accountType: z.enum(['customer', 'distributor']),
  amount: z.number().positive(),
  currency: z.nativeEnum(CurrencyCode).default(CurrencyCode.USD),
  exchangeRate: z.number().positive().optional(),
  method: z.nativeEnum(PaymentMethod),
  referenceNumber: z.string().max(120).optional(),
  relatedOrderId: idSchema.optional(),
  relatedInvoiceId: idSchema.optional(),
  notes: z.string().max(1000).optional(),
  idempotencyKey: idempotencyKeySchema.optional(),
});
export type CreatePaymentInput = z.infer<typeof createPaymentSchema>;

export const presignUploadSchema = z.object({
  accountId: idSchema,
  documentType: z.nativeEnum(DocumentType),
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1).max(120),
  relatedOrderId: idSchema.optional(),
  relatedInvoiceId: idSchema.optional(),
});
export type PresignUploadInput = z.infer<typeof presignUploadSchema>;

export const inventoryAvailabilityQuerySchema = z.object({
  skuId: idSchema,
  locationId: idSchema.optional(),
});
export type InventoryAvailabilityQuery = z.infer<typeof inventoryAvailabilityQuerySchema>;

/**
 * Manual stock movement (admin/warehouse). `receive` brings goods in (positive quantity);
 * `adjustment` corrects the count and may be negative (recount, damage, loss). `reorderPoint`
 * and `allowBackorder` only matter when this creates the SKU's first balance at a location.
 */
export const adjustStockSchema = z
  .object({
    skuId: idSchema,
    locationId: idSchema,
    type: z.enum([MovementType.RECEIVE, MovementType.ADJUSTMENT]),
    /** Units to add (positive) or remove (negative, `adjustment` only). Never zero. */
    quantity: z
      .number()
      .int('La cantidad debe ser un número entero.')
      .refine((n) => n !== 0, 'La cantidad no puede ser 0.')
      .refine((n) => Math.abs(n) <= 1_000_000, 'La cantidad es demasiado grande.'),
    reason: z.string().trim().min(3, 'Indica el motivo del movimiento.').max(300),
    reorderPoint: z.number().int().nonnegative().max(1_000_000).optional(),
    allowBackorder: z.boolean().optional(),
  })
  .refine((d) => d.type !== MovementType.RECEIVE || d.quantity > 0, {
    message: 'Una entrada de mercancía debe ser mayor que 0.',
    path: ['quantity'],
  });
export type AdjustStockInput = z.infer<typeof adjustStockSchema>;

/* ----------------------------- Admin slice (Phase 2) ----------------------------- */

export const createDistributorSchema = z.object({
  name: z.string().min(2).max(160),
  rif: rifSchema,
  territoryIds: z.array(z.string().max(80)).default([]),
  allowedLocationIds: z.array(idSchema).default([]),
});
export type CreateDistributorInput = z.infer<typeof createDistributorSchema>;

export const createSkuSchema = z.object({
  productId: idSchema,
  code: z.string().min(1).max(80),
  name: z.string().min(1).max(160),
  unit: z.string().min(1).max(40),
  packSize: z.number().int().positive().optional(),
  basePriceUsd: z.number().nonnegative(),
  active: z.boolean().default(true),
});
export type CreateSkuInput = z.infer<typeof createSkuSchema>;

/** PATCH inputs — partial; handlers must reject an empty body. */
/** On update, `distributorId: null` removes the link to the distributor. */
export const updateCustomerSchema = createCustomerSchema.partial().extend({ distributorId: idSchema.nullable().optional() });
export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>;

/** `archived: true` retires a record (and deactivates it); `false` restores it, still inactive. */
const archivedSchema = z.boolean().optional();

export const updateProductSchema = createProductSchema.partial().extend({ archived: archivedSchema });
export type UpdateProductInput = z.infer<typeof updateProductSchema>;

/** Brands and categories share one shape: a name and an active flag (the id is generated). */
const catalogNameSchema = z.string().trim().min(2).max(80);

export const createBrandSchema = z.object({
  name: catalogNameSchema,
  active: z.boolean().default(true),
});
export type CreateBrandInput = z.infer<typeof createBrandSchema>;
export const updateBrandSchema = createBrandSchema.partial().extend({ archived: archivedSchema });
export type UpdateBrandInput = z.infer<typeof updateBrandSchema>;

export const createCategorySchema = createBrandSchema;
export type CreateCategoryInput = z.infer<typeof createCategorySchema>;
export const updateCategorySchema = createCategorySchema.partial().extend({ archived: archivedSchema });
export type UpdateCategoryInput = z.infer<typeof updateCategorySchema>;

export const updateDistributorSchema = createDistributorSchema.partial();
export type UpdateDistributorInput = z.infer<typeof updateDistributorSchema>;

/** POST /customers/{id}/status and /distributors/{id}/status. Rejecting, holding or suspending needs a reason. */
export const accountStatusActionSchema = z
  .object({
    action: z.nativeEnum(AccountAction),
    reason: z.string().trim().max(500).optional(),
  })
  .superRefine((value, ctx) => {
    if (ACCOUNT_ACTIONS_REQUIRING_REASON.includes(value.action) && !value.reason) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'Indica el motivo.' });
    }
  })
  .transform((value) => ({ action: value.action, reason: value.reason || undefined }));
export interface AccountStatusActionInput {
  action: AccountAction;
  reason?: string;
}

/** Body for order approve / reject / cancel transitions (optional reason + idempotency). */
export const orderDecisionSchema = z.object({
  reason: z.string().max(1000).optional(),
  idempotencyKey: idempotencyKeySchema.optional(),
});
export type OrderDecisionInput = z.infer<typeof orderDecisionSchema>;

/** Body for payment approve / reject (finance/admin review of a manual payment). */
export const paymentDecisionSchema = z.object({
  reason: z.string().max(1000).optional(),
  idempotencyKey: idempotencyKeySchema.optional(),
});
export type PaymentDecisionInput = z.infer<typeof paymentDecisionSchema>;

/** Mark in-app notifications as read (one id, or all for the caller). */
export const markNotificationsReadSchema = z.object({
  notificationIds: z.array(idSchema).optional(),
  all: z.boolean().optional(),
});
export type MarkNotificationsReadInput = z.infer<typeof markNotificationsReadSchema>;

/** Upper bound for the USD->VES rate (VES per 1 USD); anything above is almost surely a typo. */
export const MAX_EXCHANGE_RATE = 1_000_000;

/** Set the active USD->VES exchange rate (admin/finance). `rate` = VES per 1 USD. */
export const setExchangeRateSchema = z.object({
  rate: z.number().positive().max(MAX_EXCHANGE_RATE, 'La tasa es demasiado alta.'),
  note: z.string().max(500).optional(),
});
export type SetExchangeRateInput = z.infer<typeof setExchangeRateSchema>;

/**
 * Register an uploaded document AFTER the file has been PUT to S3 via a presigned URL.
 * `documentId` + `s3Key` come from the presign step; the server re-checks the key belongs
 * to `accountId` before persisting a PENDING record.
 */
export const confirmDocumentSchema = z.object({
  accountId: idSchema,
  documentId: idSchema,
  s3Key: z.string().min(1).max(1024),
  documentType: z.nativeEnum(DocumentType),
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1).max(120),
});
export type ConfirmDocumentInput = z.infer<typeof confirmDocumentSchema>;

/** Approve or reject an uploaded document (admin/finance). */
export const reviewDocumentSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  reason: z.string().max(1000).optional(),
});
export type ReviewDocumentInput = z.infer<typeof reviewDocumentSchema>;

/* ----------------------------- Promotions ----------------------------- */
const promotionFields = z.object({
  name: z.string().min(2).max(160),
  description: z.string().max(1000).optional(),
  kind: z.nativeEnum(PromotionKind),
  /** percentage / volume_rebate: 0–100; fixed: USD minor units. */
  value: z.number().nonnegative().max(100_000_000),
  startsAt: isoDateSchema,
  endsAt: isoDateSchema,
  active: z.boolean(),
  code: z.string().max(40).optional(),
});

/** Public code customers type at checkout; stored upper-case so duplicates are caught regardless of case. */
export const promotionCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9_-]{2,39}$/, 'El código debe tener de 3 a 40 caracteres: letras, números, guion o guion bajo.');

export const createPromotionSchema = promotionFields
  .extend({ code: promotionCodeSchema.optional() })
  .refine((d) => new Date(d.endsAt).getTime() > new Date(d.startsAt).getTime(), {
    message: 'La fecha de fin debe ser posterior al inicio.',
    path: ['endsAt'],
  })
  .refine((d) => new Date(d.endsAt).getTime() > Date.now(), {
    message: 'La fecha de fin ya pasó: la promoción nacería vencida.',
    path: ['endsAt'],
  });
export type CreatePromotionInput = z.infer<typeof createPromotionSchema>;

/**
 * PATCH body. The start/end order cannot be checked here when only one of them is sent; the
 * service compares the merged result against the stored promotion.
 */
export const updatePromotionSchema = promotionFields
  .extend({ code: promotionCodeSchema.optional() })
  .partial()
  .extend({ archived: archivedSchema });
export type UpdatePromotionInput = z.infer<typeof updatePromotionSchema>;

/* ----------------------------- Invoices / billing (Phase 4) ----------------------------- */

/** Generate a billing document (proforma or internal invoice) from an existing order. */
export const createInvoiceFromOrderSchema = z.object({
  orderId: idSchema,
  kind: z.enum(['proforma', 'invoice']).default('invoice'),
  /** Override the due date; defaults to the customer's payment terms (issue date + N days). */
  dueInDays: z.number().int().min(0).max(365).optional(),
  idempotencyKey: idempotencyKeySchema.optional(),
});
export type CreateInvoiceFromOrderInput = z.infer<typeof createInvoiceFromOrderSchema>;

/** Void an invoice that has no payments applied; the reason is mandatory. */
export const voidInvoiceSchema = z.object({
  customerId: idSchema,
  reason: z.string().trim().min(1, 'Indica el motivo de la anulación.').max(500),
});
export type VoidInvoiceInput = z.infer<typeof voidInvoiceSchema>;

/**
 * Allocate an APPROVED payment across one or more invoices. `amount` is a decimal in the
 * payment's currency; the backend converts to integer minor units and enforces that the
 * total allocated does not exceed the payment and each invoice's outstanding balance.
 */
export const allocatePaymentSchema = z.object({
  allocations: z
    .array(z.object({ invoiceId: idSchema, amount: z.number().positive() }))
    .min(1),
  idempotencyKey: idempotencyKeySchema.optional(),
});
export type AllocatePaymentInput = z.infer<typeof allocatePaymentSchema>;

/* ----------------------------- Quotes (Phase 5) ----------------------------- */

/** Create a quote (cotización) — priced like an order but reserves NO stock. */
export const createQuoteSchema = z.object({
  customerId: idSchema,
  items: z.array(orderItemInputSchema).min(1),
  /** Validity window; defaults applied server-side when omitted. */
  validUntilDays: z.number().int().min(1).max(365).optional(),
  notes: z.string().max(1000).optional(),
});
export type CreateQuoteInput = z.infer<typeof createQuoteSchema>;

/** Body for quote send / accept / reject transitions (optional reason). */
export const quoteDecisionSchema = z.object({
  reason: z.string().max(1000).optional(),
  idempotencyKey: idempotencyKeySchema.optional(),
});
export type QuoteDecisionInput = z.infer<typeof quoteDecisionSchema>;

/**
 * Convert an accepted quote into a real order. The order locks the exchange rate at
 * conversion time (VES per 1 USD) and reserves stock — so the rate is supplied here.
 */
export const convertQuoteSchema = z.object({
  exchangeRate: z.number().positive(),
  idempotencyKey: idempotencyKeySchema.optional(),
});
export type ConvertQuoteInput = z.infer<typeof convertQuoteSchema>;
